import {
  HTMLAttributes,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { KaseApi } from '@/api';
import { NoResultItem } from '@/components';
import { ICTVCode } from '@/models';
import { useToastStore } from '@/store';
import { colors } from '@/theme/variables';

import {
  getCtvCodeLabel,
  shouldUpdateDisplayedText,
  shouldUpdateSearchText,
} from './locationCodeSearch.utils';

interface LocationCodeSearchProps {
  existingCtvCodes?: ICTVCode[];
  onSelect: (id?: string) => void;
  value?: ICTVCode;
  agencyCtvCodeId?: string;
}

const LocationCodeSearch: React.FC<LocationCodeSearchProps> = ({
  existingCtvCodes,
  onSelect,
  value,
  agencyCtvCodeId,
}: LocationCodeSearchProps) => {
  const [searchText, setSearchText] = useState('');
  const [inputText, setInputText] = useState('');
  const [searchedLinks, setSearchedLinks] = useState<ICTVCode[]>([]);
  const [selectedValue, setSelectedValue] = useState<ICTVCode | null>(null);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const didInitDefault = useRef(false);
  const didUserSelect = useRef(false);
  const prevAgencyId = useRef<string | undefined>(undefined);
  const { updateToast } = useToastStore();

  const handleChange = async (newValue?: ICTVCode | null) => {
    handleDebouncedSearchText.cancel();
    setSearchText('');
    setPage(1);
    setInputText(newValue ? getCtvCodeLabel(newValue) : '');
    setSelectedValue(newValue ?? null);
    didUserSelect.current = true;
    onSelect(newValue?._id || '');
  };

  useEffect(() => {
    let isActive = true;

    const loadDefaultCtvCode = async () => {
      if (!agencyCtvCodeId) return;
      const results = await fetchCtvCodes('');
      if (!isActive) return;
      const agencyCtvCode = results.find(
        (ctvCode) => ctvCode._id === agencyCtvCodeId,
      );
      setSelectedValue(agencyCtvCode ?? null);
      setInputText(agencyCtvCode ? getCtvCodeLabel(agencyCtvCode) : '');
      if (!didUserSelect.current) {
        onSelect(agencyCtvCode?._id || '');
      }
      didInitDefault.current = true;
    };

    if (value) {
      setSelectedValue(value);
      setInputText(getCtvCodeLabel(value));
      didInitDefault.current = true;
      return () => {
        isActive = false;
      };
    }

    if (prevAgencyId.current !== agencyCtvCodeId) {
      prevAgencyId.current = agencyCtvCodeId;
      if (!didUserSelect.current) {
        didInitDefault.current = false;
      }
    }

    if (!didInitDefault.current && !didUserSelect.current) {
      loadDefaultCtvCode();
    }

    return () => {
      isActive = false;
    };
  }, [agencyCtvCodeId, value]);

  const handleDebouncedSearchText = useDebouncedCallback((v: string) => {
    setPage(1);
    setSearchText(v);
  }, 875);

  const handleInputChange = (
    _: SyntheticEvent,
    v: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (shouldUpdateDisplayedText(reason)) {
      setInputText(v);
    }

    if (shouldUpdateSearchText(reason)) {
      if (reason === 'clear') {
        handleDebouncedSearchText.cancel();
        setPage(1);
        setSearchText('');
        return;
      }

      handleDebouncedSearchText(v);
    }
  };

  useEffect(() => {
    fetchCtvCodes(searchText, page);
  }, [searchText, page]);

  const fetchCtvCodes = async (
    val?: string,
    pageToLoad = 1,
  ): Promise<ICTVCode[]> => {
    const v = val?.replace(/[^a-zA-Z0-9 ]/g, '').trim();

    try {
      setLoading(true);
      const filter = JSON.stringify({
        $or: [
          { code: { $regex: v ?? '', $options: 'i' } },
          { locationLiteral: { $regex: v ?? '', $options: 'i' } },
        ],
      });
      const params = {
        sort: JSON.stringify({ createdAt: -1 }),
        page: pageToLoad,
        limit: 100,
        filter,
        ...(agencyCtvCodeId && { includeIds: [agencyCtvCodeId] }),
      };

      const { data } = await KaseApi.listCtvCodes(params);
      setCount(data.count);
      setSearchedLinks((prev) => {
        if (pageToLoad === 1) return data.results;

        const merged = [...prev];
        data.results.forEach((item) => {
          if (!merged.find((existing) => existing._id === item._id)) {
            merged.push(item);
          }
        });
        return merged;
      });
      return data.results;
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      setLoading(false);
    }
    return [];
  };

  const handleScroll = (event: SyntheticEvent) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      !loading &&
      searchedLinks.length < count
    ) {
      setPage((prev) => prev + 1);
    }
  };

  const renderOptions = (
    props: HTMLAttributes<HTMLLIElement>,
    ctvCode: ICTVCode,
  ) => {
    const { code, locationLiteral, geographicType } = ctvCode;

    return (
      <Box
        {...props}
        component="li"
        flexDirection="column"
        flexWrap="wrap"
        sx={{
          flexDirection: 'row',
          borderBottom: `1px solid ${colors.grey[10]}`,
          flexWrap: 'wrap',
          '&:hover': {
            button: { display: 'block' },
          },
        }}
      >
        <Stack
          flexDirection="row"
          sx={{
            width: '100%',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="inherit" sx={{ width: '100%' }}>
            {code} - {locationLiteral} - {geographicType}
          </Typography>
        </Stack>
      </Box>
    );
  };
  return (
    <Autocomplete
      fullWidth
      autoHighlight
      onInputChange={handleInputChange}
      inputValue={inputText}
      options={searchedLinks}
      noOptionsText={<NoResultItem />}
      filterOptions={(filteredCtvCodes) => {
        return filteredCtvCodes.reduce((acc, item) => {
          const found = existingCtvCodes?.find((linked) => {
            return linked?._id === item._id;
          });

          return found ? acc : [...acc, item];
        }, [] as ICTVCode[]);
      }}
      onChange={(_, v) => {
        handleChange(v);
      }}
      value={selectedValue}
      isOptionEqualToValue={(option, optionValue) =>
        option._id === optionValue?._id
      }
      getOptionLabel={getCtvCodeLabel}
      renderOption={renderOptions}
      renderInput={(params) => (
        <TextField {...params} placeholder="Search..." fullWidth />
      )}
      ListboxProps={{ onScroll: handleScroll }}
      clearOnBlur={false}
    />
  );
};

export default LocationCodeSearch;
